import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import { RemoveUserRoles } from "../../redux/actions/RemoveUserRolesAction";

const selectedCountry = localStorage.getItem("selectedCountry");

const isCurrentUserSuperAdmin = () => {
  const sessionData = localStorage.getItem("sesion");

  if (!sessionData || !selectedCountry) {
    return false;
  }

  const session = JSON.parse(sessionData);

  return (session.rols || []).some(
    (role) =>
      role.roleName === "SuperAdmin" &&
      role.country?.countryName === selectedCountry
  );
};

const RemoveUserRolesModal = ({ open, onClose, userData }) => {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.removeUserRoles.status);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const loading = status === "loading";

  const assignedRoles = (userData?.rols || []).map((role) => ({
    idRole: role.idrole ?? role.idRole,
    roleName: role.roleName,
  }));

  const canRemoveSuperAdmin = isCurrentUserSuperAdmin();

  const removableRoles = assignedRoles.filter(
    (role) => canRemoveSuperAdmin || role.roleName !== "SuperAdmin"
  );

  useEffect(() => {
    if (open) {
      setSelectedRoleIds([]);
      setMessage({ type: "", text: "" });
    }
  }, [open, userData]);

  const handleRoleToggle = (roleId) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId]
    );
  };

  const isFormValid = () =>
    userData?.iduser != null && selectedRoleIds.length > 0;

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    setMessage({ type: "", text: "" });

    try {
      const result = await dispatch(
        RemoveUserRoles(userData.iduser, selectedRoleIds)
      );

      if (result.success) {
        setMessage({ type: "success", text: result.message });
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setMessage({ type: "error", text: result.message });
      }
    } catch {
      setMessage({
        type: "error",
        text: "There was an error removing roles. Please try again.",
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Remove Roles</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            label="User"
            value={userData?.userName ?? ""}
            fullWidth
            disabled
            InputLabelProps={{ shrink: true }}
          />

          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            Select roles to remove
          </Typography>
          <FormGroup>
            {removableRoles.map((role) => (
              <FormControlLabel
                key={role.idRole}
                control={
                  <Checkbox
                    checked={selectedRoleIds.includes(role.idRole)}
                    onChange={() => handleRoleToggle(role.idRole)}
                    disabled={loading}
                  />
                }
                label={role.roleName}
              />
            ))}
          </FormGroup>

          {assignedRoles.length === 0 && (
            <Alert severity="info">This user has no roles assigned.</Alert>
          )}

          {assignedRoles.length > 0 &&
            removableRoles.length === 0 &&
            !canRemoveSuperAdmin && (
            <Alert severity="info">
              Only SuperAdmin roles are assigned and cannot be removed.
            </Alert>
          )}

          {message.text && (
            <Alert
              severity={message.type}
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSave}
          disabled={!isFormValid() || loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          {loading ? "Removing..." : "Remove Roles"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoveUserRolesModal;
